import { Injectable } from '@angular/core';
import { async, Subject } from 'rxjs';

// Internal models
import { ReservationCostDto } from 'src/app/models/reservations/objects/reservation-cost.dto';
import { ReservationDto } from 'src/app/models/reservations/objects/reservation.dto';
import { CorporationDetailDto } from 'src/app/models/venue/public-corporation-detail.dto';
import { CorporationMembershipsDto } from 'src/app/models/memberships/objects/corporation-membership.dto';
import { AdditionalGuestMembershipDto, MembershipSubscriptionsDto } from 'src/app/models/memberships/objects/membership-members.dto';
import { GuestPaymentMethodDto } from 'src/app/models/reservations/objects/guest-payment-method.dto';
import { ReservationAdditionalGuestDto } from 'src/app/models/reservations/objects/reservation-additional-guest.dto';
import { ActivateGuestMembershipStep } from 'src/app/models/memberships/objects/activate-guest-membership-step.object';

@Injectable({
  providedIn: 'root'
})
export class ManageReservationProvider {
  stripePublishableKey: string;

  reservationDetails: ReservationDto;
  reservationCosts: ReservationCostDto;
  corporationDetails: CorporationDetailDto;
  manageReservationDataLoadedObservable = new Subject<boolean>();

  // venue memberships variables
  venueMemberships: CorporationMembershipsDto[];
  venueMembershipsUpdatedObservable = new Subject<boolean>();

  // primary guest memberships variables
  primaryGuestMemberships: MembershipSubscriptionsDto[];
  primaryGuestMembershipsUpdatedObservable = new Subject<boolean>();

  // additional guest memberships variables
  additionalGuestMemberships: AdditionalGuestMembershipDto[];
  additionalGuestMembershipsUpdatedObservable = new Subject<boolean>();

  // payment methods variables
  showAddPaymentMethodModal = false;
  showAddPaymentMethodObservable = new Subject<boolean>();

  showRemovePaymentMethodModal = false;
  selectedPaymentMethodForRemoval: GuestPaymentMethodDto;
  showRemovePaymentMethodObservable = new Subject<boolean>();

  // payment details variables
  showPayRemainingBalanceModal = false;
  showPayRemainingBalanceObservable = new Subject<boolean>();

  // membership status variables
  showUpgradeMembershipStatusModal = false;
  showUpgradeMembershipStatusObservable = new Subject<boolean>();

  // guest information variables
  showAddGuestModal = false;
  showAddGuestObservable = new Subject<boolean>();

  showInviteGuestModal = false;
  inviteGuestReservationData: ReservationDto;
  showInviteGuestObservable = new Subject<boolean>();

  showActivateGuestMembershipModal = false;
  showActivateGuestMembershipObservable = new Subject<boolean>();
  activateGuestMembershipButtonDisabled = false;
  activateGuestMembershipButtonDisabledObservable = new Subject<boolean>();
  
  showRemoveGuestModal = false;
  removeGuestData: ReservationAdditionalGuestDto;
  showRemoveGuestObservable = new Subject<boolean>();

  constructor() { }

  emitDataLoaded(
    stripePublishableKey: string,
    reservationDetails: ReservationDto,
    reservationCosts: ReservationCostDto,
    corporationDetails: CorporationDetailDto,
    venueMemberships: CorporationMembershipsDto[],
    primaryGuestMemberships: MembershipSubscriptionsDto[],
    additionalGuestMemberships: AdditionalGuestMembershipDto[],
    dataLoaded: boolean
  ) {
    this.stripePublishableKey = stripePublishableKey;
    this.reservationDetails = reservationDetails;
    this.reservationCosts = reservationCosts;
    this.corporationDetails = corporationDetails;
    this.venueMemberships = venueMemberships;
    this.primaryGuestMemberships = primaryGuestMemberships;
    this.additionalGuestMemberships = additionalGuestMemberships;
    this.manageReservationDataLoadedObservable.next(dataLoaded);
  }

  async getstripePublishableKey() {
    return this.stripePublishableKey;
  }

  async getReservationDetails() {
    return this.reservationDetails;
  }

  async getReservationCosts() {
    return this.reservationCosts;
  }

  async getCorporationDetails() {
    return this.corporationDetails;
  }

  async getAllDataLoaded() {
    const dataLoaded = {
      stripePublishableKey: this.stripePublishableKey,
      reservationDetails: this.reservationDetails,
      reservationCosts: this.reservationCosts,
      corporationDetails: this.corporationDetails,
      venueMemberships: this.venueMemberships,
      primaryGuestMemberships: this.primaryGuestMemberships,
      additionalGuestMemberships: this.additionalGuestMemberships
    };
    return dataLoaded;
  }


  // payment methods functions
  emitShowAddPaymentMethod(show: boolean) {
    this.showAddPaymentMethodModal = show;
    this.showAddPaymentMethodObservable.next(show);
  }

  emitShowRemovePaymentMethod(show: boolean, selectedPaymentMethodForRemoval: GuestPaymentMethodDto) {
    this.showRemovePaymentMethodModal = show;
    this.selectedPaymentMethodForRemoval = selectedPaymentMethodForRemoval;
    this.showRemovePaymentMethodObservable.next(show);
  }

  async getSelectedPaymentMethodForRemoval() {
    return this.selectedPaymentMethodForRemoval;
  }


  // payment details functions
  emitShowPayRemainingBalance(show: boolean) {
    this.showPayRemainingBalanceModal = show;
    this.showPayRemainingBalanceObservable.next(show);
  }

  
  // membership status functions
  emitShowUpgradeMembershipStatus(show: boolean) {
    this.showUpgradeMembershipStatusModal = show;
    this.showUpgradeMembershipStatusObservable.next(show);
  }

  async venueHasMinimumRequirement(): Promise<boolean> {
    return this.venueMemberships.some(membership => membership.isMinimumRequirement);
  }

  async guestMeetsMinimumRequirement(guestType: 'primary' | 'additional'): Promise<boolean> {
    switch(guestType) {
      case 'primary':
        const guestMembership = this.primaryGuestMemberships
          .find(membership => membership.corporationMembershipId === this.venueMemberships
        .find(venueMembership => (venueMembership.isMinimumRequirement || venueMembership.supersedesMinimumRequirement) && venueMembership.isActive)?.corporationMembershipId);
        return guestMembership ? guestMembership.isActive : false;
      case 'additional':
        // TODO
        // const additionalGuestMembership = this.additionalGuestMemberships
        //   .find(membership => membership.memberships.some(membership => membership.corporationMembershipId === this.venueMemberships
        //     .find(venueMembership => (venueMembership.isMinimumRequirement || venueMembership.supersedesMinimumRequirement) && venueMembership.isActive)?.corporationMembershipId));
        // return additionalGuestMembership ? additionalGuestMembership.memberships.some(membership => membership.isActive) : false;
        return false;
    }
  }

  async emitVenueMembershipsUpdated(updatedMemberships: CorporationMembershipsDto[]) {
    this.venueMemberships = updatedMemberships;
    this.venueMembershipsUpdatedObservable.next(true);
  }

  async emitPrimaryGuestMembershipsUpdated(updatedMemberships: MembershipSubscriptionsDto[]) {
    this.primaryGuestMemberships = updatedMemberships;
    this.primaryGuestMembershipsUpdatedObservable.next(true);
  }

  async emitAdditionalGuestMembershipsUpdated(updatedMemberships: AdditionalGuestMembershipDto[]) {
    this.additionalGuestMemberships = updatedMemberships;
    this.additionalGuestMembershipsUpdatedObservable.next(true);
  }

  async getVenueMemberships(): Promise<CorporationMembershipsDto[]> {
    return this.venueMemberships;
  }

  async getPrimaryGuestMemberships(): Promise<MembershipSubscriptionsDto[]> {
    return this.primaryGuestMemberships;
  }

  async getAdditionalGuestMemberships(): Promise<AdditionalGuestMembershipDto[]> {
    return this.additionalGuestMemberships;
  }


  // guest information functions
  emitShowAddGuest(show: boolean) {
    this.showAddGuestModal = show;
    this.showAddGuestObservable.next(show);
  }

  emitShowInviteGuest(show: boolean, inviteGuestReservationData: ReservationDto) {
    this.showInviteGuestModal = show;
    this.inviteGuestReservationData = inviteGuestReservationData;
    this.showInviteGuestObservable.next(show);
  }

  async getInviteGuestReservationData(): Promise<ReservationDto> {
    return this.inviteGuestReservationData;
  }

  emitShowActivateGuestMembership(show: boolean) {
    this.showActivateGuestMembershipModal = show;
    this.showActivateGuestMembershipObservable.next(show);
  }

  emitActivateGuestMembershipButtonDisabled(disabled: boolean) {
    this.activateGuestMembershipButtonDisabled = disabled;
    this.activateGuestMembershipButtonDisabledObservable.next(disabled);
  }

  async getActivateGuestMembershipButtonDisabled(): Promise<boolean> {
    return this.activateGuestMembershipButtonDisabled;
  }

  async closeAndResetActivateGuestMembership() {
    this.showActivateGuestMembershipModal = false;
    this.showActivateGuestMembershipObservable.next(false);
    this.activateGuestMembershipButtonDisabled = false;
    this.activateGuestMembershipButtonDisabledObservable.next(false);
  }

  emitShowRemoveGuest(show: boolean, removeGuestData: ReservationAdditionalGuestDto) {
    this.showRemoveGuestModal = show;
    this.removeGuestData = removeGuestData;
    this.showRemoveGuestObservable.next(show);
  }

  async getRemoveGuestData(): Promise<ReservationAdditionalGuestDto> {
    return this.removeGuestData;
  }
}
